import { useLayoutEffect, useMemo, useRef } from 'react';
import { shortMonthDay, stripWeeks } from './weeklyReportModel';

/**
 * The Weekly Report's week picker: a row of week cells drawn like the crew
 * schedule's weekly header ("wk of" over the Sunday's date, dark cells, the
 * selected one green), each a button. It scrolls sideways with a visible
 * scrollbar to move quickly across a year of weeks.
 *
 * Weeks are Sunday-anchored, the crew schedule's convention, so a cell here
 * and a weekly column there name the same seven days.
 */

const css = `
  .wr-strip {
    display: flex; overflow-x: auto; overflow-y: hidden;
    border-bottom: 4px solid var(--ew-green);
    border-radius: var(--ew-r-md) var(--ew-r-md) 0 0;
    background: var(--ew-dark);
    scrollbar-width: thin; scrollbar-color: var(--ew-green) var(--ew-n-100);
  }
  .wr-strip::-webkit-scrollbar { height: 10px; }
  .wr-strip::-webkit-scrollbar-track { background: var(--ew-n-100); }
  .wr-strip::-webkit-scrollbar-thumb { background: var(--ew-green); border-radius: 5px; }
  .wr-wk {
    flex: 0 0 104px; height: 46px; padding: 6px 4px; box-sizing: border-box;
    border: 0; border-right: 1px solid rgba(255,255,255,0.09);
    background: var(--ew-dark); color: var(--ew-white);
    font-family: var(--ew-font); text-align: center; cursor: pointer;
    position: relative;
  }
  .wr-wk:hover { background: #4a4a4a; }
  .wr-wk:focus-visible { outline: 2px solid var(--ew-white); outline-offset: -4px; }
  .wr-wk[aria-pressed="true"] { background: var(--ew-green); }
  .wr-wk-top { font-size: 9px; opacity: 0.7; font-weight: 500; letter-spacing: 0.04em; }
  .wr-wk-date { font-size: 12px; font-weight: 700; margin-top: 2px; }
  .wr-wk-now::after {
    content: ''; position: absolute; left: 20%; right: 20%; bottom: 4px; height: 3px;
    border-radius: 2px; background: var(--ew-green);
  }
  .wr-wk-now[aria-pressed="true"]::after { background: var(--ew-white); }
  /* A week whose finalized report is owed: a yellow flag in the corner. */
  .wr-wk-mark {
    position: absolute; top: 4px; right: 5px; min-width: 14px; height: 14px; padding: 0 3px; box-sizing: border-box;
    border-radius: 7px; font: 800 10px/14px var(--ew-font); text-align: center;
  }
  .wr-wk-mark-flag { background: var(--ew-safety); color: var(--ew-dark); }
  .wr-wk-mark-done { color: var(--ew-white); opacity: 0.75; }
`;

/** Per week: 'flag' = its finalized report is owed; 'done' = uploaded, or marked "No report". */
export type WeekMark = 'flag' | 'done';

export default function WeekStrip({ selected, thisWeek, onSelect, marks = {} }: {
  selected: string;
  thisWeek: string;
  onSelect: (weekStart: string) => void;
  marks?: Record<string, WeekMark>;
}) {
  const weeks = useMemo(() => stripWeeks(thisWeek, selected), [thisWeek, selected]);
  const scroller = useRef<HTMLDivElement>(null);
  const first = useRef(true);

  // Keep the selected week in view, centered. scrollLeft rather than
  // scrollIntoView, which would also scroll the page to the strip.
  //
  // ⚠️ Measured against the STRIP, not with offsetLeft: offsetLeft counts from
  // the nearest positioned ancestor, which is not the strip, so the nav rail's
  // width was added in and the selected week sat left of center.
  useLayoutEffect(() => {
    const box = scroller.current;
    const cell = box?.querySelector<HTMLElement>(`[data-week="${selected}"]`);
    if (!box || !cell) return;
    const cellLeftInStrip = cell.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
    const left = cellLeftInStrip - (box.clientWidth - cell.offsetWidth) / 2;
    if (typeof box.scrollTo === 'function') box.scrollTo({ left, behavior: first.current ? 'auto' : 'smooth' });
    else box.scrollLeft = left;
    first.current = false;
  }, [selected]);

  return (
    <>
      <style>{css}</style>
      <div className="wr-strip" ref={scroller} role="group" aria-label="Report week">
        {weeks.map((ws) => {
          // A year back, "Sep 28" would read as this September: mark another
          // year's weeks, on screen and to a screen reader alike.
          const otherYear = ws.slice(0, 4) !== thisWeek.slice(0, 4);
          const mark = marks[ws];
          return (
            <button
              key={ws}
              type="button"
              data-week={ws}
              data-mark={mark}
              className={`wr-wk${ws === thisWeek ? ' wr-wk-now' : ''}`}
              aria-pressed={ws === selected}
              // Contains the cell's visible "Sep 27", so a voice-control user
              // can click it by what they see (WCAG 2.5.3, label in name).
              aria-label={`Week of ${shortMonthDay(ws)}${otherYear ? `, ${ws.slice(0, 4)}` : ''}${ws === thisWeek ? ' (this week)' : ''}`
                + (mark === 'flag' ? ', finalized report not uploaded' : mark === 'done' ? ', finalized report filed' : '')}
              // "Filed" covers both an upload and a "No report" mark.
              title={mark === 'flag' ? 'Finalized report not uploaded yet' : mark === 'done' ? 'Finalized report filed' : undefined}
              onClick={() => onSelect(ws)}
            >
              {mark && <span className={`wr-wk-mark wr-wk-mark-${mark}`} aria-hidden="true">{mark === 'flag' ? '!' : '✓'}</span>}
              <div className="wr-wk-top">{ws === thisWeek ? 'this week' : 'wk of'}</div>
              <div className="wr-wk-date">{shortMonthDay(ws)}{otherYear ? ` ’${ws.slice(2, 4)}` : ''}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}
